import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import * as XLSX from 'xlsx';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Upload, Download, RefreshCw, CheckCircle, XCircle, AlertTriangle,
  HelpCircle, FileSpreadsheet, ChevronDown, ChevronUp, Lightbulb, Building2
} from "lucide-react";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useClient } from "@/context/ClientContext";

// Import extracted components
import { ReconciliationSummary, TDSReturnUpload } from "@/components/reconciliation";
import { reconStatusBg, diffColor } from "@/utils/reconStyles";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Reconciliation = () => {
  const { selectedClient, clientId } = useClient();
  const [periods, setPeriods] = useState({ financial_years: [], quarters: [] });
  const [selectedFY, setSelectedFY] = useState("");
  const [selectedQuarter, setSelectedQuarter] = useState("");
  const [tdsReturns, setTdsReturns] = useState([]);
  const [reconciliation, setReconciliation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState("upload");
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [uploadedFile, setUploadedFile] = useState(null);
  const fileInputRef = useRef(null);

  const fetchPeriods = useCallback(async () => {
    if (!clientId) return;
    try {
      const params = { client_id: clientId };
      const response = await axios.get(`${API}/periods`, { params });
      setPeriods(response.data);
      if (response.data.financial_years.length > 0 && !selectedFY) {
        setSelectedFY(response.data.financial_years[0]);
      }
    } catch (err) {
      toast.error("Failed to load periods");
    }
  }, [clientId, selectedFY]);

  const fetchTdsReturns = useCallback(async () => {
    if (!selectedFY || !selectedQuarter || !clientId) return;
    try {
      const response = await axios.get(`${API}/tds-return`, {
        params: { financial_year: selectedFY, quarter: selectedQuarter, client_id: clientId }
      });
      setTdsReturns(response.data);
    } catch (err) {
      toast.error("Failed to load TDS returns");
    }
  }, [selectedFY, selectedQuarter, clientId]);

  const fetchReconciliation = async () => {
    if (!selectedFY || !selectedQuarter) {
      toast.error("Please select Financial Year and Quarter");
      return;
    }
    if (!clientId) {
      toast.error("Please select a client first");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reconciliation`, {
        params: { financial_year: selectedFY, quarter: selectedQuarter, client_id: clientId }
      });
      setReconciliation(response.data);
      setActiveTab("results");
    } catch (err) {
      toast.error("Failed to perform reconciliation");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    queueMicrotask(() => {
      fetchPeriods();
      setTdsReturns([]);
      setReconciliation(null);
    });
  }, [fetchPeriods]);

  useEffect(() => {
    queueMicrotask(() => fetchTdsReturns());
  }, [fetchTdsReturns]);

  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  const handleFileUpload = async () => {
    if (!uploadedFile) {
      toast.error("Please select a file first");
      return;
    }
    if (!selectedFY || !selectedQuarter) {
      toast.error("Please select Financial Year and Quarter first");
      return;
    }
    if (!clientId) {
      toast.error("Please select a client first");
      return;
    }

    setUploading(true);
    try {
      const data = await uploadedFile.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });

      if (jsonData.length < 2) {
        toast.error("File appears to be empty or has no data rows");
        setUploading(false);
        return;
      }

      // Find headers
      const headerRow = jsonData[0].map(h => String(h || '').toLowerCase().trim());
      
      // Map common column names
      const findColumn = (names) => {
        for (const name of names) {
          const idx = headerRow.findIndex(h => h.includes(name.toLowerCase()));
          if (idx !== -1) return idx;
        }
        return -1;
      };

      const panCol = findColumn(['pan', 'deductee pan']);
      const nameCol = findColumn(['name', 'party', 'deductee']);
      const tdsCol = findColumn(['tds', 'tax deducted', 'tax amount']);
      const amtCol = findColumn(['amount', 'gross', 'payment', 'total']);

      if (panCol === -1) {
        toast.error("Could not find PAN column in the file");
        setUploading(false);
        return;
      }

      const entries = [];
      for (let i = 1; i < jsonData.length; i++) {
        const row = jsonData[i];
        const pan = row[panCol] ? String(row[panCol]).trim().toUpperCase() : '';
        if (!pan || pan.length !== 10) continue;

        entries.push({
          pan,
          party_name: nameCol !== -1 ? String(row[nameCol] || '').trim() : '',
          tds_amount: tdsCol !== -1 ? parseFloat(row[tdsCol]) || 0 : 0,
          gross_amount: amtCol !== -1 ? parseFloat(row[amtCol]) || 0 : 0
        });
      }

      if (entries.length === 0) {
        toast.error("No valid entries found in the file");
        setUploading(false);
        return;
      }

      // Upload to server
      await axios.post(`${API}/tds-return/bulk`, {
        financial_year: selectedFY,
        quarter: selectedQuarter,
        client_id: clientId,
        entries
      });

      toast.success(`Uploaded ${entries.length} entries successfully`);
      fetchTdsReturns();
      setUploadedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to upload TDS return");
    } finally {
      setUploading(false);
    }
  };

  const toggleRowExpansion = (idx) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(idx)) newSet.delete(idx);
      else newSet.add(idx);
      return newSet;
    });
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const config = {
      matched: { icon: CheckCircle, color: 'bg-emerald-100 text-emerald-700', label: 'Matched' },
      mismatch: { icon: AlertTriangle, color: 'bg-amber-100 text-amber-700', label: 'Mismatch' },
      missing_in_return: { icon: XCircle, color: 'bg-red-100 text-red-700', label: 'Missing in Return' },
      missing_in_tally: { icon: HelpCircle, color: 'bg-blue-100 text-blue-700', label: 'Missing in Tally' }
    };
    const c = config[status] || config.mismatch;
    const Icon = c.icon;
    return (
      <Badge className={c.color}>
        <Icon className="w-3 h-3 mr-1" />
        {c.label}
      </Badge>
    );
  };

  // Calculate suggestion counts
  const suggestionCounts = useMemo(() => {
    if (!reconciliation?.details) return { total: 0, high: 0, medium: 0, low: 0 };
    let total = 0, high = 0, medium = 0, low = 0;
    reconciliation.details.forEach(item => {
      if (item.suggestions) {
        item.suggestions.forEach(s => {
          total++;
          if (s.priority === 'high') high++;
          else if (s.priority === 'medium') medium++;
          else low++;
        });
      }
    });
    return { total, high, medium, low };
  }, [reconciliation]);

  if (!clientId) {
    return (
      <div className="p-6">
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="pt-6 flex items-center gap-4">
            <Building2 className="w-8 h-8 text-amber-500" />
            <div>
              <p className="font-medium text-amber-800">No Client Selected</p>
              <p className="text-sm text-amber-600">Please select a client from the sidebar to perform reconciliation</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="reconciliation-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileSpreadsheet className="w-6 h-6" />
            TDS Reconciliation
          </h1>
          <p className="text-slate-500 mt-1">
            Compare Tally transactions with TDS returns for {selectedClient?.name}
          </p>
        </div>
      </div>

      {/* Period Selector */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="w-48">
              <label className="text-sm text-slate-500 mb-1 block">Financial Year</label>
              <Select value={selectedFY} onValueChange={setSelectedFY}>
                <SelectTrigger data-testid="fy-select">
                  <SelectValue placeholder="Select FY" />
                </SelectTrigger>
                <SelectContent>
                  {periods.financial_years.map(fy => (
                    <SelectItem key={fy} value={fy}>{fy}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <label className="text-sm text-slate-500 mb-1 block">Quarter</label>
              <Select value={selectedQuarter} onValueChange={setSelectedQuarter}>
                <SelectTrigger data-testid="quarter-select">
                  <SelectValue placeholder="Select Quarter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Q1">Q1 (Apr-Jun)</SelectItem>
                  <SelectItem value="Q2">Q2 (Jul-Sep)</SelectItem>
                  <SelectItem value="Q3">Q3 (Oct-Dec)</SelectItem>
                  <SelectItem value="Q4">Q4 (Jan-Mar)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button 
              onClick={fetchReconciliation} 
              disabled={loading || !selectedFY || !selectedQuarter}
              data-testid="reconcile-btn"
            >
              {loading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Run Reconciliation
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="upload" data-testid="upload-tab">
            <Upload className="w-4 h-4 mr-2" />
            Upload TDS Return
          </TabsTrigger>
          <TabsTrigger value="returns" data-testid="returns-tab">
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            TDS Returns ({tdsReturns.length})
          </TabsTrigger>
          <TabsTrigger value="results" disabled={!reconciliation} data-testid="results-tab">
            <CheckCircle className="w-4 h-4 mr-2" />
            Results
          </TabsTrigger>
        </TabsList>

        {/* Upload Tab */}
        <TabsContent value="upload">
          <TDSReturnUpload
            fileInputRef={fileInputRef}
            uploadedFile={uploadedFile}
            uploading={uploading}
            onFileSelect={setUploadedFile}
            onUpload={handleFileUpload}
          />
        </TabsContent>

        {/* Returns Tab */}
        <TabsContent value="returns">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Uploaded TDS Returns for {selectedFY} - {selectedQuarter}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {tdsReturns.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <FileSpreadsheet className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No TDS returns uploaded for this period</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => setActiveTab("upload")}
                  >
                    Upload TDS Return
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="p-3 text-left">PAN</th>
                        <th className="p-3 text-left">Party Name</th>
                        <th className="p-3 text-right">TDS Amount</th>
                        <th className="p-3 text-right">Gross Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {tdsReturns.slice(0, 50).map((item, idx) => (
                        <tr key={`${item.pan}-${idx}`} className="hover:bg-slate-50">
                          <td className="p-3 font-mono text-xs">{item.pan}</td>
                          <td className="p-3">{item.party_name || '-'}</td>
                          <td className="p-3 text-right font-mono">{formatCurrency(item.tds_amount)}</td>
                          <td className="p-3 text-right font-mono">{formatCurrency(item.gross_amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {tdsReturns.length > 50 && (
                    <p className="text-center text-sm text-slate-500 py-2">
                      Showing 50 of {tdsReturns.length} entries
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Results Tab */}
        <TabsContent value="results">
          {reconciliation && (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="p-4 bg-emerald-50 border-emerald-200">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                    <div>
                      <p className="text-xs text-emerald-600">Matched</p>
                      <p className="text-2xl font-bold text-emerald-700">{reconciliation.summary.matched}</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-4 bg-amber-50 border-amber-200">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                    <div>
                      <p className="text-xs text-amber-600">Mismatched</p>
                      <p className="text-2xl font-bold text-amber-700">{reconciliation.summary.mismatched}</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-4 bg-red-50 border-red-200">
                  <div className="flex items-center gap-2">
                    <XCircle className="w-5 h-5 text-red-600" />
                    <div>
                      <p className="text-xs text-red-600">Missing in Return</p>
                      <p className="text-2xl font-bold text-red-700">{reconciliation.summary.missing_in_return}</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-4 bg-blue-50 border-blue-200">
                  <div className="flex items-center gap-2">
                    <HelpCircle className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="text-xs text-blue-600">Missing in Tally</p>
                      <p className="text-2xl font-bold text-blue-700">{reconciliation.summary.missing_in_tally || 0}</p>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Suggestions Summary */}
              {suggestionCounts.total > 0 && (
                <Card className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
                  <div className="flex items-center gap-4">
                    <Lightbulb className="w-8 h-8 text-amber-500" />
                    <div>
                      <p className="font-semibold text-amber-900">
                        {suggestionCounts.total} Correction Suggestions Found
                      </p>
                      <div className="flex gap-3 mt-1 text-sm">
                        {suggestionCounts.high > 0 && <span className="text-red-700">{suggestionCounts.high} High Priority</span>}
                        {suggestionCounts.medium > 0 && <span className="text-amber-700">{suggestionCounts.medium} Medium</span>}
                        {suggestionCounts.low > 0 && <span className="text-slate-600">{suggestionCounts.low} Low</span>}
                      </div>
                    </div>
                  </div>
                </Card>
              )}

              {/* Details Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">
                    Deductee-wise Reconciliation ({reconciliation.details.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="recon-details-table">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="p-3 w-8"></th>
                          <th className="p-3 text-left">Status</th>
                          <th className="p-3 text-left">Deductee Name</th>
                          <th className="p-3 text-left">PAN</th>
                          <th className="p-3 text-left">Section (1961)</th>
                          <th className="p-3 text-left">Section (2025)</th>
                          <th className="p-3 text-right">Tally TDS</th>
                          <th className="p-3 text-right">Return TDS</th>
                          <th className="p-3 text-right">Difference</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {reconciliation.details.map((item, idx) => (
                          <React.Fragment key={`${item.pan || 'nopan'}-${item.deductee_name}-${idx}`}>
                            <tr className={`hover:bg-slate-50 ${reconStatusBg(item.status)}`}>
                              <td className="p-3">
                                {item.suggestions && item.suggestions.length > 0 && (
                                  <button
                                    onClick={() => toggleRowExpansion(idx)}
                                    className="p-1 hover:bg-slate-200 rounded"
                                  >
                                    {expandedRows.has(idx) ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                  </button>
                                )}
                              </td>
                              <td className="p-3">{getStatusBadge(item.status)}</td>
                              <td className="p-3 font-medium">{item.deductee_name || '-'}</td>
                              <td className="p-3 font-mono text-xs">{item.pan || '-'}</td>
                              <td className="p-3">
                                {item.section ? <Badge variant="outline">{item.section}</Badge> : '-'}
                              </td>
                              <td className="p-3 text-xs text-slate-600">{item.new_section || '-'}</td>
                              <td className="p-3 text-right font-mono">{formatCurrency(item.tally_tds)}</td>
                              <td className="p-3 text-right font-mono">{formatCurrency(item.return_tds)}</td>
                              <td className={`p-3 text-right font-mono font-semibold ${diffColor(item.difference_tds)}`}>
                                {item.difference_tds ? formatCurrency(item.difference_tds) : '-'}
                              </td>
                            </tr>
                            {/* Expanded Suggestions */}
                            {expandedRows.has(idx) && item.suggestions && (
                              <tr>
                                <td colSpan="9" className="bg-amber-50 p-4">
                                  <div className="flex items-start gap-2">
                                    <Lightbulb className="w-5 h-5 text-amber-500 mt-0.5" />
                                    <div>
                                      <p className="font-medium text-amber-800 mb-2">Suggestions</p>
                                      <ul className="space-y-1">
                                        {item.suggestions.map((s, sidx) => (
                                          <li key={`sug-${sidx}`} className="text-sm text-amber-700">
                                            • {typeof s === 'string' ? s : s.message || JSON.stringify(s)}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Export Button */}
              <div className="flex justify-end">
                <Button variant="outline">
                  <Download className="w-4 h-4 mr-2" />
                  Export to Excel
                </Button>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Reconciliation;
